/**
 * ComposerInbox — cross-Piece chat thread inbox.
 *
 * Shows every chat thread the current user has across all Pieces in the
 * active workspace, with unread badges and last-message snippets. Workspace
 * owners get a second "All workspace threads" tab that surfaces threads
 * started by collaborators (read-only). Clicking a row deep-links into the
 * Composer with the Piece opened and the chat panel restored to that thread
 * via `#chat=<threadId>`.
 *
 * Polish (2026-06-11):
 *   - Filter segmented control (All / Unread / Archived), persisted in
 *     `?filter=` so reloads keep state.
 *   - Bulk-select checkboxes + "Mark N as read" action (own threads only —
 *     read state is per-user, so foreign rows are excluded from selection).
 *   - Keyboard nav: j/k or ↑/↓ to move selection, Enter to open, e to
 *     archive/unarchive, r to mark read. Disabled while focus is in the
 *     search input or any other typing target.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquare, Archive, User as UserIcon, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { AppSeo } from "@/components/AppSeo";
import { composerChatApi, type ComposerChatThread } from "@/lib/composerChatApi";

type Scope = "mine" | "all";
type Filter = "all" | "unread" | "archived";

const isTypingTarget = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return (el as HTMLElement).isContentEditable === true;
};

const parseFilter = (raw: string | null): Filter =>
  raw === "unread" || raw === "archived" ? raw : "all";

const ComposerInbox = () => {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState<ComposerChatThread[]>([]);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<Scope>("mine");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [focusIdx, setFocusIdx] = useState<number>(-1);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const filter: Filter = parseFilter(searchParams.get("filter"));
  const setFilter = useCallback(
    (next: Filter) => {
      const params = new URLSearchParams(searchParams);
      if (next === "all") params.delete("filter");
      else params.set("filter", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const isWorkspaceOwner = Boolean(active && user && active.owner_user_id === user.id);

  const reload = useCallback(async () => {
    if (!active?.id) return;
    try {
      const { threads: rows } = await composerChatApi.listThreads(active.id, null, { scope });
      setThreads(rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load inbox");
    }
  }, [active?.id, scope]);

  useEffect(() => {
    if (!active?.id) return;
    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    void (async () => {
      try {
        const { threads: rows } = await composerChatApi.listThreads(active.id, null, { scope });
        if (!cancelled) setThreads(rows);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof Error ? err.message : "Could not load inbox");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active?.id, scope]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return threads
      .filter((t) => {
        if (filter === "archived") return Boolean(t.archived_at);
        if (filter === "unread") return Boolean(t.unread) && !t.archived_at;
        return !t.archived_at;
      })
      .filter((t) => {
        if (!q) return true;
        return (
          (t.title ?? "").toLowerCase().includes(q) ||
          (t.piece_title ?? "").toLowerCase().includes(q) ||
          (t.last_message_snippet ?? "").toLowerCase().includes(q) ||
          (t.author_display_name ?? "").toLowerCase().includes(q)
        );
      });
  }, [threads, search, filter]);

  // Keep focus index sane as the filtered list changes.
  useEffect(() => {
    setFocusIdx((idx) => {
      if (filtered.length === 0) return -1;
      if (idx < 0) return -1;
      return Math.min(idx, filtered.length - 1);
    });
  }, [filtered.length]);

  // Drop any selected IDs that left the filtered set so the toolbar counter
  // and "Mark N as read" stay honest.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filtered.map((t) => t.id));
      const next = new Set<string>();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const unreadCount = threads.filter((t) => t.unread && !t.archived_at).length;
  const archivedCount = threads.filter((t) => t.archived_at).length;

  const openThread = useCallback((t: ComposerChatThread) => {
    navigate(`/composer?openPiece=${t.piece_id}#chat=${t.id}`);
  }, [navigate]);

  const handleArchive = useCallback(async (t: ComposerChatThread) => {
    if (!active?.id) return;
    if (!t.is_mine) {
      toast.error("Only the thread author can archive their thread");
      return;
    }
    try {
      if (t.archived_at) {
        await composerChatApi.unarchiveThread(active.id, t.piece_id, t.id);
      } else {
        await composerChatApi.archiveThread(active.id, t.piece_id, t.id);
      }
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update thread");
    }
  }, [active?.id, reload]);

  const handleMarkRead = useCallback(async (t: ComposerChatThread) => {
    if (!active?.id || !t.is_mine) return;
    try {
      await composerChatApi.markRead(active.id, t.id);
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not mark read");
    }
  }, [active?.id, reload]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Mine-only ids in the current filtered list (foreign rows excluded from bulk).
  const selectableIds = useMemo(
    () => filtered.filter((t) => t.is_mine).map((t) => t.id),
    [filtered],
  );
  const allMineSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    if (allMineSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const bulkMarkRead = useCallback(async () => {
    if (!active?.id || selected.size === 0) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const results = await Promise.allSettled(
        ids.map((id) => composerChatApi.markRead(active.id, id)),
      );
      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        toast.error(`${failures} of ${ids.length} failed to mark read`);
      } else {
        toast.success(`Marked ${ids.length} as read`);
      }
      setSelected(new Set());
      await reload();
    } finally {
      setBusy(false);
    }
  }, [active?.id, selected, reload]);

  // Keyboard nav.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      if (filtered.length === 0) return;
      const k = e.key;
      if (k === "j" || k === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(filtered.length - 1, i < 0 ? 0 : i + 1));
      } else if (k === "k" || k === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i < 0 ? 0 : i - 1));
      } else if (k === "Enter") {
        if (focusIdx < 0) return;
        e.preventDefault();
        openThread(filtered[focusIdx]);
      } else if (k === "e" || k === "E") {
        if (focusIdx < 0) return;
        const t = filtered[focusIdx];
        if (!t.is_mine) return;
        e.preventDefault();
        void handleArchive(t);
      } else if (k === "r" || k === "R") {
        if (focusIdx < 0) return;
        const t = filtered[focusIdx];
        if (!t.is_mine || !t.unread) return;
        e.preventDefault();
        void handleMarkRead(t);
      } else if (k === "x" || k === "X") {
        if (focusIdx < 0) return;
        const t = filtered[focusIdx];
        if (!t.is_mine) return;
        e.preventDefault();
        toggleSelect(t.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, focusIdx, openThread, handleArchive, handleMarkRead]);

  // Scroll focused row into view.
  const rowRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  useEffect(() => {
    if (focusIdx < 0 || focusIdx >= filtered.length) return;
    const el = rowRefs.current.get(filtered[focusIdx].id);
    el?.scrollIntoView({ block: "nearest" });
  }, [focusIdx, filtered]);

  return (
    <>
      <AppSeo title="Chat inbox" />
      <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <Button variant="ghost" size="sm" asChild className="-ml-2">
              <Link to="/composer">
                <ArrowLeft className="h-4 w-4" />
                Back to Composer
              </Link>
            </Button>
            <h1 className="font-serif text-2xl font-semibold">Chat inbox</h1>
            <p className="text-sm text-muted-foreground">
              {scope === "mine"
                ? `Every Composer chat thread you've started. ${unreadCount > 0 ? `${unreadCount} unread.` : ""}`
                : "Every thread in this workspace, including collaborators (read-only)."}
            </p>
          </div>
        </header>

        {isWorkspaceOwner ? (
          <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
            <TabsList>
              <TabsTrigger value="mine">My threads</TabsTrigger>
              <TabsTrigger value="all">All workspace threads</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}

        <Card className="shadow-soft">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filter</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                value={filter}
                onValueChange={(v) => v && setFilter(v as Filter)}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="all" className="text-xs">All</ToggleGroupItem>
                <ToggleGroupItem value="unread" className="text-xs">
                  Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
                </ToggleGroupItem>
                <ToggleGroupItem value="archived" className="text-xs">
                  Archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
                </ToggleGroupItem>
              </ToggleGroup>
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  scope === "all"
                    ? "Search by thread, Piece, author, or snippet…"
                    : "Search by thread, Piece, or snippet…"
                }
                className="max-w-sm flex-1"
              />
            </div>
            <p className="hidden md:block text-[11px] text-muted-foreground">
              Keys: <kbd className="rounded border bg-muted px-1">j</kbd>/<kbd className="rounded border bg-muted px-1">k</kbd> move ·{" "}
              <kbd className="rounded border bg-muted px-1">Enter</kbd> open ·{" "}
              <kbd className="rounded border bg-muted px-1">e</kbd> archive ·{" "}
              <kbd className="rounded border bg-muted px-1">r</kbd> mark read ·{" "}
              <kbd className="rounded border bg-muted px-1">x</kbd> toggle select
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">
              {loading ? "Loading…" : `${filtered.length} thread${filtered.length === 1 ? "" : "s"}`}
            </CardTitle>
            {selected.size > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {selected.size} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void bulkMarkRead()}
                  disabled={busy}
                >
                  {busy ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <MailOpen className="mr-1 h-3.5 w-3.5" />
                  )}
                  Mark {selected.size} as read
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set())}
                  disabled={busy}
                >
                  Clear
                </Button>
              </div>
            ) : selectableIds.length > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleSelectAll}
              >
                Select all on page
              </Button>
            ) : null}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                <MessageSquare className="h-8 w-8 opacity-40" />
                <p className="mt-3 text-sm">
                  {filter === "unread"
                    ? "No unread threads."
                    : filter === "archived"
                      ? "No archived threads."
                      : scope === "all"
                        ? "No threads in this workspace yet."
                        : "No chat threads yet. Start a conversation from a Piece in the Composer."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((t, idx) => {
                  const isFocused = idx === focusIdx;
                  const isSelected = selected.has(t.id);
                  return (
                    <li
                      key={t.id}
                      ref={(el) => {
                        if (el) rowRefs.current.set(t.id, el);
                        else rowRefs.current.delete(t.id);
                      }}
                      className={cn(
                        "group/row flex items-center gap-3 px-1 py-3 transition-colors",
                        isFocused && "bg-accent/40 ring-1 ring-primary/30 rounded-md",
                      )}
                    >
                      {t.is_mine ? (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(t.id)}
                          aria-label="Select thread"
                          className="shrink-0"
                        />
                      ) : (
                        <span className="inline-block h-4 w-4 shrink-0" />
                      )}
                      <button
                        type="button"
                        onClick={() => openThread(t)}
                        onMouseEnter={() => setFocusIdx(idx)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-colors hover:opacity-80"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <MessageSquare className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-medium">
                              {t.title || "Untitled thread"}
                            </p>
                            {t.unread ? (
                              <Badge variant="default" className="h-5 px-1.5 text-[10px]">
                                New
                              </Badge>
                            ) : null}
                            {t.archived_at ? (
                              <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                                Archived
                              </Badge>
                            ) : null}
                            {scope === "all" && !t.is_mine ? (
                              <Badge variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]">
                                <UserIcon className="h-2.5 w-2.5" />
                                {t.author_display_name || "Collaborator"}
                              </Badge>
                            ) : null}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">
                            <span className="font-medium">
                              {t.piece_title || "Untitled Piece"}
                            </span>
                            {t.last_message_snippet ? ` · ${t.last_message_snippet}` : ""}
                          </p>
                        </div>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                          {t.last_message_at
                            ? new Date(t.last_message_at).toLocaleString()
                            : new Date(t.updated_at).toLocaleString()}
                        </span>
                      </button>
                      {t.is_mine ? (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleArchive(t)}
                          title={t.archived_at ? "Unarchive" : "Archive"}
                          className="opacity-0 group-hover/row:opacity-100"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ComposerInbox;
